import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

// Lazy-load useAuth so Header still renders on pages outside AuthProvider
// (e.g. pages loaded before provider mounts). We guard with a try/catch.
let _useAuth = null;
const getUseAuth = () => {
  if (!_useAuth) {
    try {
      // Dynamic require keeps this file free of hard circular deps
      _useAuth = require('../../../features/auth/context/AuthContext').useAuth;
    } catch {
      _useAuth = () => ({ user: null, openLogin: () => {}, logout: () => {} });
    }
  }
  return _useAuth;
};

/* ── styled bits ── */
const AuthButtonsWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProfileLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  padding: 6px 14px 6px 6px;
  border-radius: 40px;
  border: 2px solid #e5e7eb;
  background: #fff;
  transition: border-color 0.15s;

  &:hover {
    border-color: #082189;
  }
`;

const ProfileAvatar = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #082189 0%, #1a3aaa 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.5px;
  flex-shrink: 0;
`;

const ProfileName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LoginButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  background: linear-gradient(135deg, #082189 0%, #1a3aaa 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: -0.1px;
  box-shadow: 0 3px 12px rgba(8, 33, 137, 0.28);
  transition: opacity 0.15s, transform 0.12s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
    transform: scale(1.01);
  }
`;

/* ── Auth-aware button cluster ── */
const HeaderAuthButtons = () => {
  let user = null;
  let openLogin = () => {};

  try {
    const useAuth = getUseAuth();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ({ user, openLogin } = useAuth());
  } catch {
    // Not inside AuthProvider — render nothing
    return null;
  }

  if (user) {
    const initials = (user.name || '?')
      .split(' ')
      .map(w => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

    return (
      <AuthButtonsWrap>
        <ProfileLink
          href="/profile"
          title={`Logged in as ${user.name || user.mobile}`}
        >
          <ProfileAvatar>
            {initials}
          </ProfileAvatar>
          <ProfileName>
            {user.name ? user.name.split(' ')[0] : 'Profile'}
          </ProfileName>
        </ProfileLink>
      </AuthButtonsWrap>
    );
  }

  return (
    <LoginButton onClick={openLogin}>
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
      Login
    </LoginButton>
  );
};

/* ── Mobile offcanvas auth link ── */
const OffcanvasProfileLink = styled.a`
  font-size: 16px;
  font-weight: 700;
  color: #082189;
  text-decoration: none;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
  display: block;
`;

const OffcanvasLoginButton = styled.button`
  font-size: 16px;
  font-weight: 700;
  color: #082189;
  background: none;
  border: none;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  text-align: left;
  width: 100%;
  font-family: inherit;
`;

const OffcanvasAuthLink = () => {
  let user = null;
  let openLogin = () => {};

  try {
    const useAuth = getUseAuth();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ({ user, openLogin } = useAuth());
  } catch {
    return null;
  }

  if (user) {
    return (
      <OffcanvasProfileLink href="/profile">
        My Profile
      </OffcanvasProfileLink>
    );
  }

  return (
    <OffcanvasLoginButton onClick={openLogin}>
      Login / Sign Up
    </OffcanvasLoginButton>
  );
};

/* ── Cart icon with live item count ── */
const CartLink = styled.a`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  border: 2px solid #e5e7eb;
  background: #fff;
  text-decoration: none;
  color: #111827;
  transition: border-color 0.15s;

  &:hover {
    border-color: #082189;
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #082189;
  color: #fff;
  font-size: 10.5px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(8, 33, 137, 0.4);
`;

const CartIcon = () => {
  let count = 0;
  try {
    // eslint-disable-next-line global-require
    const { useCartStore } = require('../../../features/cart/store/cartStore');
    // eslint-disable-next-line react-hooks/rules-of-hooks
    count = useCartStore((s) => s.itemCount());
  } catch { count = 0; }

  return (
    <CartLink href="/cart" title="Cart">
      <svg width={19} height={19} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </svg>
      {count > 0 ? (
        <CartBadge>{count}</CartBadge>
      ) : null}
    </CartLink>
  );
};

/* ── Header layout styled bits ── */
const HeaderLogoImg = styled.img`
  width: 150px;
  max-width: 100%;
  height: auto;
  display: block;

  @media (max-width: 767px) {
    width: 120px;
  }
  @media (max-width: 390px) {
    width: 104px;
  }
`;

const HeaderNav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 36px;
`;

const HeaderNavLink = styled.a`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  text-decoration: none;
  letter-spacing: -0.1px;
`;

const HeaderNavCta = styled.a`
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  text-decoration: none;
  background: #082189;
  padding: 8px 16px;
  border-radius: 8px;
  letter-spacing: -0.1px;
  white-space: nowrap;
  transition: opacity 0.15s;

  /* Vendor CSS forces a:hover{color:inherit} → white CTA text turns dark.
     Keep it white and readable. */
  &:hover,
  &:focus {
    color: #ffffff;
    opacity: 0.92;
  }
`;

const HeaderNavCtaOutline = styled.a`
  font-size: 13px;
  font-weight: 700;
  color: #082189;
  text-decoration: none;
  background: #ffffff;
  border: 1.5px solid #082189;
  padding: 6.5px 15px;
  border-radius: 8px;
  letter-spacing: -0.1px;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;

  &:hover,
  &:focus {
    background: #082189;
    color: #ffffff;
  }
`;

// Clean, responsive "Contact us" button (replaces the heavy vendor tp-btn-black).
const ContactBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 20px;
  background: #082189;
  color: #ffffff;
  box-shadow: 0 3px 12px rgba(8, 33, 137, 0.28);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  transition: opacity 0.15s, transform 0.12s;

  &:hover { opacity: 0.9; transform: scale(1.02); color: #fff; }

  svg { transition: transform 0.2s ease; }
  &:hover svg { transform: translate(2px, -2px); }

  /* Tighter on tablet widths where it shares the row with cart + hamburger */
  @media (max-width: 1200px) {
    padding: 9px 14px;
  }
  @media (max-width: 991px) {
    padding: 8px 12px;
    gap: 5px;
  }
`;

const HeaderRight = styled.div`
  gap: 12px;
  flex-wrap: nowrap;
  min-width: 0;
`;

const HeaderRightInner = styled.div`
  align-items: center;
  gap: 10px;

  @media (max-width: 991px) {
    gap: 8px;
  }
`;

const OffcanvasNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
`;

const OffcanvasLink = styled.a`
  font-size: 16px;
  font-weight: 700;
  color: ${({ $brand }) => ($brand ? '#082189' : '#111827')};
  text-decoration: none;
  padding: 10px 0;
  ${({ $noBorder }) => ($noBorder ? '' : 'border-bottom: 1px solid #f3f4f6;')}
`;

/* Self-contained hamburger — the vendor theme painted the bars white (so they
   vanished on the white header until a scroll-added `header-sticky` class kicked
   in) and its open/close relied on jQuery that this React app never loads.
   These dark bars are always visible and the button is wired via React state. */
const Hamburger = styled.button`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 5px;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  span {
    display: block;
    height: 2.5px;
    border-radius: 2px;
    background: #082189;
    transition: width 0.2s ease;
  }
  span:nth-child(1) { width: 22px; }
  span:nth-child(2) { width: 28px; }
  span:nth-child(3) { width: 16px; }

  &:hover span { width: 28px; }
`;

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  // Lock body scroll while the offcanvas is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      <div id="header-sticky" className="tp-header-area tp-header-ptb sticky-white-bg tp-header-border">
        <div className="container container-1750">
          <div className="row align-items-center">
            <div className="col-xl-2 col-lg-6 col-6">
              <div className="tp-header-logo">
                <a href="/">
                  <HeaderLogoImg className="logo-white" src="/assets/img/home-01/hero/hero-video-1.png" alt="logo" />
                </a>
              </div>
            </div>
            <div className="col-xl-7 d-none d-xl-block">
              <div className="tp-header-box text-center">
                <div className="tp-header-menu tp-header-dropdown dropdown-white-bg">
                  <HeaderNav className="tp-mobile-menu-active">
                    {/* <HeaderNavLink href="/deals">Deals</HeaderNavLink> */}
                    <HeaderNavLink href="/about">About</HeaderNavLink>
                    <HeaderNavLink href="/reviews">Reviews</HeaderNavLink>
                    <HeaderNavLink href="/blog">Blogs</HeaderNavLink>
                    <HeaderNavCtaOutline href="/corporate-enquiry">Corporate Enquiry</HeaderNavCtaOutline>
                    <HeaderNavCta href="/join-professional">Join as Professional</HeaderNavCta>
                  </HeaderNav>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-lg-6 col-6">
              <HeaderRight className="tp-header-right d-flex align-items-center justify-content-end">
                <HeaderRightInner className="d-none d-md-flex">
                  <CartIcon />
                  <HeaderAuthButtons />
                  <ContactBtn href="/contact">
                    <span>Contact us</span>
                    <svg width={10} height={10} viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 9L9 1M9 1H1M9 1V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </ContactBtn>
                </HeaderRightInner>
                <div className="tp-header-bar ml-20 d-xl-none">
                  <Hamburger
                    type="button"
                    aria-label="Open menu"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen(true)}
                  >
                    <span />
                    <span />
                    <span />
                  </Hamburger>
                </div>
              </HeaderRight>
            </div>
          </div>
        </div>
      </div>

      <div className={`tp-offcanvas-area${menuOpen ? ' opened' : ''}`}>
        <div className="tp-offcanvas-wrapper">
          <div className="tp-offcanvas-top d-flex align-items-center justify-content-between">
            <div className="tp-offcanvas-logo">
              <a href="/">
                <img className="logo-1" data-width={120} src="/assets/img/home-01/hero/hero-video-1.png" alt="logo" />
                <img className="logo-2" data-width={120} src="/assets/img/home-01/hero/hero-video-1.png" alt="logo" />
              </a>
            </div>
            <div className="tp-offcanvas-close">
              <button className="tp-offcanvas-close-btn" type="button" aria-label="Close menu" onClick={closeMenu}>
                <svg width={37} height={38} viewBox="0 0 37 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.19141 9.80762L27.5762 28.1924" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M9.19141 28.1924L27.5762 9.80761" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
          <div className="tp-offcanvas-main">
            <div className="tp-offcanvas-content d-none d-xl-block">
              <h3 className="tp-offcanvas-title">Hello There!</h3>
              <p>Welcome to City Craft. We are here to serve you.</p>
            </div>
            <div className="tp-offcanvas-menu d-xl-none">
              <OffcanvasNav>
                <OffcanvasLink href="/" onClick={closeMenu}>Home</OffcanvasLink>
                <OffcanvasLink href="/search" onClick={closeMenu}>Search</OffcanvasLink>
                <OffcanvasLink href="/deals" onClick={closeMenu}>Deals &amp; Offers</OffcanvasLink>
                <OffcanvasLink href="/about" onClick={closeMenu}>About Us</OffcanvasLink>
                <OffcanvasLink href="/reviews" onClick={closeMenu}>Reviews</OffcanvasLink>
                <OffcanvasLink href="/blog" onClick={closeMenu}>Blogs</OffcanvasLink>
                <OffcanvasLink href="/corporate-enquiry" onClick={closeMenu}>Corporate Enquiry</OffcanvasLink>
                <OffcanvasLink href="/join-professional" $brand onClick={closeMenu}>Join as Professional</OffcanvasLink>
                <OffcanvasLink href="/cart" onClick={closeMenu}>Cart</OffcanvasLink>
                <OffcanvasLink href="/bookings" onClick={closeMenu}>My Bookings</OffcanvasLink>
                <OffcanvasLink href="/support" onClick={closeMenu}>Support</OffcanvasLink>
                <OffcanvasAuthLink />
                <OffcanvasLink href="/contact" $noBorder onClick={closeMenu}>Contact Us</OffcanvasLink>
              </OffcanvasNav>
            </div>
            <div className="tp-offcanvas-contact">
              <h3 className="tp-offcanvas-title sm">Information</h3>
              <ul>
                <li><a href="tel:+4401482589961">08062271234</a></li>
                <li><a href="mailto:info@citycraft.in">info@citycraft.in</a></li>
                <li><a href="/contact">Gamut Di Lusso, Road No 12, Banjara Hills, Hyderabad</a></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`body-overlay${menuOpen ? ' opened' : ''}`}
        onClick={closeMenu}
      />
    </>
  );
};

export default Header;
